import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Crown, Check, Info, CreditCard, History, HelpCircle } from 'lucide-react'
import PayPalPaymentDialog from '@/components/common/PayPalPaymentDialog'
import { MembershipPlan } from '@/types'
import { getPaymentMethods } from '@/services/paymentService'
import Toast from '@/components/Toast'

const BillingPage: React.FC = () => {
  const { t } = useTranslation();
  const [selectedPlan, setSelectedPlan] = useState<MembershipPlan | null>(null)
  const [isPayPalDialogOpen, setIsPayPalDialogOpen] = useState(false)
  const [paymentMethods, setPaymentMethods] = useState<string[]>([])
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  // 会员计划配置
  const membershipPlans: MembershipPlan[] = [
    {
      id: 1,
      name: '基础版',
      description: '适合个人用户入门体验',
      price: 0,
      features: [
        '每月10个视频生成额度',
        '基础模板库访问',
        '720p视频导出',
        '社区支持'
      ]
    },
    {
      id: 2,
      name: '专业版',
      description: '适合专业创作者和小团队',
      price: 99,
      features: [
        '每月100个视频生成额度',
        '高级模板库访问',
        '4K视频导出',
        '去除水印',
        '优先处理队列',
        '邮件支持'
      ],
      recommended: true
    },
    {
      id: 3,
      name: '企业版',
      description: '适合大型团队和企业客户',
      price: 299,
      features: [
        '无限视频生成额度',
        '所有模板库访问',
        '4K视频导出',
        '去除水印',
        'API访问权限',
        '团队协作功能',
        '专属客户经理',
        '定制化服务'
      ]
    }
  ]

  // 当前用户计划（模拟数据）
  const currentPlan = membershipPlans[0] // 假设当前是基础版

  // 获取支付方式
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const methods = await getPaymentMethods()
        // 过滤出可用的支付方式
        const availableMethods = methods.methods
          .filter(method => ['paypal', 'stripe'].includes(method.code))
          .map(method => method.name)
        setPaymentMethods(availableMethods)
      } catch (error) {
        console.error('获取支付方式失败:', error)
      }
    }

    fetchPaymentMethods()
  }, [])

  // 显示提示消息
  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  // 处理升级计划
  const handleUpgradePlan = (plan: MembershipPlan) => {
    if (plan.id === currentPlan.id) {
      showToast(t('toast.membership.alreadyOnPlan'), 'error')
      return
    }

    if (plan.price === 0) {
      // 免费计划直接切换
      showToast(t('toast.membership.switchedToBasic'), 'success')
      return
    }

    setSelectedPlan(plan)
    setIsPayPalDialogOpen(true)
  }

  // 处理支付成功
  const handlePaymentSuccess = (message: string) => {
    setIsPayPalDialogOpen(false)
    showToast(message || t('toast.membership.paymentSuccess'), 'success')
  }

  // 处理支付错误
  const handlePaymentError = (error: string) => {
    showToast(error || t('toast.membership.paymentFailed'), 'error')
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* 页面标题 */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">订阅与账单</h1>
        <p className="text-gray-600">管理您的订阅计划和支付方式</p>
      </div>

      {/* 当前订阅状态 */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 mb-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center mb-2">
              <Crown className="mr-2" size={24} />
              <h2 className="text-xl font-semibold">当前计划</h2>
            </div>
            <p className="text-2xl font-bold mb-1">{currentPlan.name}</p>
            <p className="text-blue-100">{currentPlan.description}</p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold">
              {currentPlan.price === 0 ? '免费' : `¥${currentPlan.price}/月`}
            </p>
            <p className="text-blue-100 text-sm mt-1">
              下次续费时间：2024-12-18
            </p>
          </div>
        </div>
      </div>

      {/* 会员计划选择 */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">选择您的计划</h2>

        <div className="grid md:grid-cols-3 gap-6">
          {membershipPlans.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-lg border-2 p-6 transition-all ${
                plan.id === currentPlan.id
                  ? 'border-blue-500 bg-blue-50'
                  : plan.recommended
                  ? 'border-purple-500 bg-purple-50 hover:shadow-lg'
                  : 'border-gray-200 bg-white hover:shadow-lg'
              }`}
            >
              {/* 推荐标签 */}
              {plan.recommended && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                    推荐
                  </span>
                </div>
              )}

              {/* 当前计划标签 */}
              {plan.id === currentPlan.id && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                    当前计划
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <p className="text-gray-600 mb-4">{plan.description}</p>
                <div className="text-4xl font-bold text-gray-900">
                  {plan.price === 0 ? '免费' : `¥${plan.price}`}
                  {plan.price > 0 && <span className="text-lg font-normal text-gray-600">/月</span>}
                </div>
              </div>

              {/* 功能列表 */}
              <div className="space-y-3 mb-6">
                {plan.features.map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <Check className="text-green-500 mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              {/* 选择按钮 */}
              <button
                onClick={() => handleUpgradePlan(plan)}
                disabled={plan.id === currentPlan.id}
                className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                  plan.id === currentPlan.id
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : plan.recommended
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {plan.id === currentPlan.id ? '当前计划' :
                 plan.price === 0 ? '使用免费版' : '升级计划'}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* 支付方式 */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">支持的支付方式</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {paymentMethods.length > 0 ? (
            paymentMethods.map((method, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 text-center">
                <CreditCard className="mx-auto mb-2 text-gray-600" size={32} />
                <p className="text-sm font-medium text-gray-900">{method}</p>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center text-gray-500">
              正在加载支付方式...
            </div>
          )}
        </div>
      </div>

      {/* 账单历史 */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">账单历史</h2>
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="p-6 text-center text-gray-500">
            <History className="mx-auto mb-2 text-gray-400" size={48} />
            <p>暂无账单记录</p>
            <p className="text-sm mt-1">您的账单历史将显示在这里</p>
          </div>
        </div>
      </div>

      {/* 帮助信息 */}
      <div className="bg-gray-50 rounded-lg p-6">
        <div className="flex items-center mb-4">
          <HelpCircle className="mr-2 text-gray-600" size={20} />
          <h3 className="text-lg font-semibold text-gray-900">需要帮助？</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
          <div className="flex items-start">
            <Info className="mr-2 mt-0.5 text-gray-400" size={16} />
            <div>
              <p className="font-medium text-gray-900 mb-1">退款政策</p>
              <p>购买后7天内可申请全额退款</p>
            </div>
          </div>
          <div className="flex items-start">
            <Info className="mr-2 mt-0.5 text-gray-400" size={16} />
            <div>
              <p className="font-medium text-gray-900 mb-1">发票开具</p>
              <p>支持开具企业发票，请联系客服</p>
            </div>
          </div>
        </div>
      </div>

      {/* PayPal支付对话框 */}
      <PayPalPaymentDialog
        isOpen={isPayPalDialogOpen}
        onClose={() => setIsPayPalDialogOpen(false)}
        plan={selectedPlan!}
        billingCycle={1} // 默认月付
        onSuccess={handlePaymentSuccess}
        onError={handlePaymentError}
      />

      {/* Toast 提示 */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  )
}

export default BillingPage